import { Box, Stack } from '@chronosphereio/chrono-ui';
import { useMemo, useState } from 'react';
import useResizeObserver from 'use-resize-observer';
import { NoData } from '@/plugin-system/components/NoData';
import { buildClusterLayout, layoutFocusedSubset } from './build-cluster-layout';
import { ClusterPagination } from './ClusterPagination';
import { DependencyMapCluster } from './DependencyMapCluster';
import { DependencyMapLegend } from './DependencyMapLegend';
import { useDependencyMapConfig } from './dependency-map-config';
import { Focus, NodeFocusSelect } from './NodeFocusSelect';
import { splitIntoClusters } from './split-into-clusters';
import { DependencyLink, isAggregateLink } from './types';

interface DependencyMapContentProps {
  links: DependencyLink[];
  /** Per-node self-traffic values (`from === to` in the source data); forwarded to the cluster. */
  selfLoops: ReadonlyMap<string, number>;
}

/** Body of `DependencyMap`. Owns cross-cluster focus + page state. */
export const DependencyMapContent = ({ links, selfLoops }: DependencyMapContentProps) => {
  const { layout, nodeType } = useDependencyMapConfig();

  // Measure the actual graph-area height so the per-column cap matches the
  // SVG-available space — the panel `height` prop would over-count chrome.
  const { ref: graphAreaRef, width: graphAreaWidth, height: graphAreaHeight } = useResizeObserver<HTMLDivElement>();

  // Bucket the height by `nodeSpacingPx` so sub-bucket resizes don't re-run
  // `splitIntoClusters` + `buildClusterLayout`. `null` pre-measurement falls
  // back to the layout default, avoiding a first-paint `Other (N)` blob.
  const cap =
    graphAreaHeight === undefined || graphAreaHeight <= 0
      ? null
      : Math.min(layout.maxNodesPerColumn, Math.max(1, Math.floor(graphAreaHeight / layout.nodeSpacingPx)));

  const effectiveLayout = useMemo(() => {
    if (cap === null) return layout;

    return { ...layout, maxNodesPerColumn: cap };
  }, [layout, cap]);

  // One cluster per connected component. Empty layouts filtered so callers
  // can rely on `nodes[0]` existing.
  const clusters = useMemo(
    () =>
      splitIntoClusters(links)
        .map((subset) => buildClusterLayout(subset, effectiveLayout))
        .filter((cluster) => cluster.nodes.length > 0),
    [links, effectiveLayout]
  );

  const [focus, setFocus] = useState<Focus | null>(null);

  // Clamped each render to ensure active page exists.
  const [clusterPage, setClusterPage] = useState(0);
  const activePage = clusters.length === 0 ? 0 : Math.min(clusterPage, clusters.length - 1);

  // `splitIntoClusters` sorts by size, so cluster indices shuffle on data
  // refresh (auto-refresh, time range change). Re-resolve focus against the
  // current ordering; without this a stale `clusterIdx` can match `activePage`
  // while pointing to a *different* cluster than the one containing
  // `focus.nodeId`, producing an empty subset (no rawLink has the focused id)
  // and a silently blank SVG.
  const [prevClusters, setPrevClusters] = useState(clusters);
  if (clusters !== prevClusters) {
    setPrevClusters(clusters);

    if (focus !== null) {
      const currentIdx = clusters.findIndex((c) =>
        c.rawLinks.some((l) => l.from === focus.nodeId || l.to === focus.nodeId)
      );

      if (currentIdx === -1) {
        setFocus(null);
      } else if (currentIdx !== focus.clusterIdx) {
        setFocus({ clusterIdx: currentIdx, nodeId: focus.nodeId });
        setClusterPage(currentIdx);
      }
    }
  }

  const visibleCluster = clusters[activePage];

  // Focus is per-cluster — paging away shouldn't apply it to the next cluster's ids.
  const visibleFocusedId = focus?.clusterIdx === activePage ? focus.nodeId : null;

  // Display subset for the on-screen cluster.
  const displaySubset = useMemo(() => {
    if (!visibleCluster) return null;
    if (visibleFocusedId === null) {
      return { nodes: visibleCluster.nodes, links: visibleCluster.links };
    }

    return layoutFocusedSubset(
      visibleCluster.nodes,
      visibleCluster.rawLinks,
      visibleCluster.aggregateMembers,
      visibleFocusedId,
      effectiveLayout
    );
  }, [visibleCluster, visibleFocusedId, effectiveLayout]);

  // Nothing to render — surface the standard "No data" message and skip the
  // focus picker / pagination chrome (both are meaningless without clusters).
  if (clusters.length === 0) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0, minWidth: 0 }}>
        <NoData />
      </Box>
    );
  }

  return (
    // `minWidth: 0` here and on the graph-area Box below — without it, the
    // cluster's pixel-width SVG would pin every ancestor at the last observed
    // width via flex `min-width: auto`, blocking shrinkage.
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <NodeFocusSelect
          clusters={clusters}
          focus={focus}
          nodeType={nodeType}
          onFocusChange={(next) => {
            setFocus(next);
            // Jump the pager to the focused node's cluster.
            if (next !== null) setClusterPage(next.clusterIdx);
          }}
        />
        <ClusterPagination
          page={activePage}
          total={clusters.length}
          onPageChange={(next) => {
            setClusterPage(next);
            // Drop focus on page change; otherwise the highlight is stale.
            setFocus(null);
          }}
        />
      </Stack>
      {visibleCluster && displaySubset && (
        <>
          {/* Graph area; ref is the resize-observed measurement that feeds the
           * per-column cap. `minWidth: 0` is the same anti-pin trick as above. */}
          <Box ref={graphAreaRef} sx={{ flex: 1, display: 'flex', minHeight: 0, minWidth: 0 }}>
            <DependencyMapCluster
              // Anchored to the first node's id: survives in-cluster focus changes,
              // remounts on page change (resets pan/hover). Empty clusters are
              // filtered above; `?? ''` is just a `noUncheckedIndexedAccess` widener.
              key={visibleCluster.nodes[0]?.id ?? ''}
              width={graphAreaWidth ?? 0}
              height={graphAreaHeight ?? 0}
              nodes={displaySubset.nodes}
              links={displaySubset.links}
              selfLoops={selfLoops}
              focusedId={visibleFocusedId}
              onFocusedIdChange={(id) => setFocus(id === null ? null : { clusterIdx: activePage, nodeId: id })}
            />
          </Box>
          {/* Legend sits outside the cluster so the cluster's measured height
           * equals the SVG's available space exactly. */}
          <Box sx={{ justifyContent: 'center', display: 'flex' }}>
            <DependencyMapLegend hasAggregateLinks={displaySubset.links.some(isAggregateLink)} />
          </Box>
        </>
      )}
    </Stack>
  );
};
